import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createCrudHooks } from './use-crud-factory'
import { supabase } from '@/lib/supabase'
import type { ServiceRecipe, RecipeImage } from '@/types/database.types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SupabaseQueryChain = { select: any; insert: any; update: any; delete: any; eq: any; order: any; single: any }

/* ============================================================
   useServiceRecipes — PRD §8.3 Look-to-Book
   CRUD for service_recipes + recipe_images tables.
   ============================================================ */

/* ── Service Recipes (via CRUD factory) ── */

const {
  useList: useServiceRecipeList,
  useOne: useServiceRecipe,
  useCreate: useCreateServiceRecipe,
  useUpdate: useUpdateServiceRecipe,
  useDelete: useDeleteServiceRecipe,
} = createCrudHooks<ServiceRecipe>({
  table: 'service_recipes',
  queryKey: 'service_recipes',
  orderBy: 'name',
  orderAsc: true,
})

export {
  useServiceRecipeList,
  useServiceRecipe,
  useCreateServiceRecipe,
  useUpdateServiceRecipe,
  useDeleteServiceRecipe,
}

export { useServiceRecipeList as useServiceRecipes }

/* ── Recipe Images (custom hooks for recipe-scoped queries) ── */

/** List images for a specific recipe */
export function useRecipeImages(recipeId: string | undefined) {
  return useQuery<RecipeImage[]>({
    queryKey: ['recipe_images', 'list', recipeId],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('recipe_images') as unknown as SupabaseQueryChain)
        .select('*')
        .eq('recipe_id', recipeId!)
        .order('is_primary', { ascending: false })
      if (error) throw error
      return (data ?? []) as RecipeImage[]
    },
    enabled: !!recipeId,
  })
}

/** Create a new recipe image */
export function useCreateRecipeImage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (values: Omit<RecipeImage, 'id' | 'created_at'>) => {
      const { data, error } = await (supabase
        .from('recipe_images') as unknown as SupabaseQueryChain)
        .insert(values as Record<string, unknown>)
        .select()
        .single()
      if (error) throw error
      return data as RecipeImage
    },
    onSuccess: (_data: RecipeImage, variables: Omit<RecipeImage, 'id' | 'created_at'>) => {
      qc.invalidateQueries({ queryKey: ['recipe_images', 'list', variables.recipe_id] })
    },
  })
}

/** Delete a recipe image */
export function useDeleteRecipeImage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, recipeId }: { id: string; recipeId: string }) => {
      const { error } = await (supabase
        .from('recipe_images') as unknown as SupabaseQueryChain)
        .delete()
        .eq('id', id)
      if (error) throw error
      return recipeId
    },
    onSuccess: (recipeId: string) => {
      qc.invalidateQueries({ queryKey: ['recipe_images', 'list', recipeId] })
    },
  })
}

/** Set an image as the primary image for a recipe */
export function useSetPrimaryRecipeImage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, recipeId }: { id: string; recipeId: string }) => {
      // Unset all primaries for this recipe first
      await (supabase.from('recipe_images') as unknown as SupabaseQueryChain)
        .update({ is_primary: false })
        .eq('recipe_id', recipeId)

      // Set the selected one as primary
      const { error } = await (supabase.from('recipe_images') as unknown as SupabaseQueryChain)
        .update({ is_primary: true })
        .eq('id', id)
      if (error) throw error
      return recipeId
    },
    onSuccess: (recipeId: string) => {
      qc.invalidateQueries({ queryKey: ['recipe_images', 'list', recipeId] })
    },
  })
}
